import {ENodeType, EUnknownNodeType} from "/@lib@/commons/xml/dom.js";
import {IJmlObj, IJmlSet} from "/@lib@/commons/xml/jml.js";
import {IXAddr, IXAddrSeq, XA} from "/@lib@/commons/xml/xAddr.js";
import {MsgOt} from "/@lib@/edit/ot/houseOt.js";

/**
 * Une struct est un modèle de noeud, une directive (group, choice, unorderedGroup)
 * ou un modèle d'attribut.
 * Un ISkStructDef caractérise une struct pour notamment construire des messages.
 *
 * Cette interface est implémentée par SkRule de la couche "schema", mais aussi
 * par d'autres objets simulant un modèle dynamiquement.
 */
export interface ISkStructDef {

	structType: ENodeType | EFuzzyType | EDirectiveType

	/**
	 * Nom technique de la structure.
	 * Peut-être les noms xpath des nodes "myTag", "x:myTag", "@myAtt", "text()", "x:*".
	 * Pour une directive, la syntaxe conpact NG peut utilisé (tagA | tagB...)
	 * Ce nom n'est pas nécessairement unique et ne peut être utilisé pour constituer le nom
	 * des éléments XML ou attributs.
	 * @see ISkStructDef.structMatch()
	 */
	structName: string

	/**
	 * Utilisé pour catégoriser et ordonner les actions associées (menu, ...))
	 */
	structGroup?: string

	/**
	 * Libellé localisé pour les utilisateurs finaux de cette structure.
	 * A utiliser de préférence à structName pour des messages aux utilisateurs.
	 * Optionnel, doit retourner null si pas différent de structName (et pas structName)
	 * pour signifier qu'il n'est pas forcément intelligible pour un utilisateur final.
	 */
	structLabel?: string

	structMatch(nodeType: ENodeType | EUnknownNodeType, nodeName?: string): boolean

	/**
	 * Construit un contenu vide correspondant à cette structure avec les éventuelles sous-structures obligatoires (non fuzzy).
	 *
	 * Si ISkStructDef.structType est un EFuzzyType, alors une simple chaine string vide ou partielle est retournée
	 * pour créer l'élément ou l'attribut générique correspondant (attsToAppend n'est pas enrichi).
	 * Pour les autres ISkStructDef.structType, la méthode retourne void.
	 */
	createContent(childrenToAppend?: IJmlSet, attsToAppend?: IJmlObj): void | string

	/**
	 * Construit un contenu potentiellement non vide suite à une transformation, internalisation / externalisation (...)
	 * du contenu qui est sur le point d'être remplacé.
	 *
	 * Méthode à utiliser à la place de createContent() si elle existe.
	 *
	 * XXX ajouter en paramètre la source du remplacement pour déporter la recherche d'une transformation à ce stade de l'insertion ?
	 *
	 * @param childrenToAppend
	 * @param attsToAppend
	 * @return false si le user a abandonné, cf doc createContent() pour les autres valeurs
	 */
	createContentAsync?(childrenToAppend: IJmlSet, attsToAppend: IJmlObj, uiContext: HTMLElement): Promise<void | string | false>
}

export function isSkStructDef(s: any): s is ISkStructDef {return s && typeof (s as ISkStructDef).structType === 'number'}

export const enum EFuzzyType {
	elements = 41,
	attributes = 42
}

export const enum EDirectiveType {
	choice = 51,
	group = 52,
	unorderedGroup = 53,
	empty = 54
}

/**
 * Annotation associée à un noeud produite suite à une validation par le schéma ou autre mécanisme d'ajout d'annotations (HintAnnot).
 *
 * Si ISkAnnot.start pointe un attribut, alors ISkAnnot.len ne peut être égal qu'à 1 ou 0 (ISkAnnot de type attribut manquant par ex.).
 *
 * Une ISkAnnot est immutable : aucune propriété ne peut être modifiée, l'ancrage
 * de l'annotation étant sur le noeud réel, pas son addresse. Autrement dit,
 * l'addresse ISkAnnot.anchor || ISkAnnot.start peut changer si un noeud précédent est inséré ou supprimé,
 * mais l'annotation est identique, toujours accrochée au même noeud.
 */
export interface ISkAnnot extends IXAddrSeq {

	type: string

	start: IXAddr | null //null si l'annot a été killed.

	len: number

	/**
	 * Noeud source **existant** sur lequel l'annotation est ancrée (donc le parent si l'annot est dans un interstice).
	 */
	anchorNode: Node

	/**
	 * Ancrage de l'annotation sur le parent lorsque l'annot est dans un interstice entre deux noeuds de la source.
	 * Utilisé uniquement pour les ISkAnnot
	 * - de type "Missing" (node et attr)
	 * - de type "diffForeign" (node et attr) pour insérer des noeuds/attr étrangers à l'item courant
	 * Dans les autres cas, ISkAnnot.anchor correspond à ISkAnnot.start, la prop n'est pas dupliquée.
	 * Dans le cas général, il faut donc utiliser : "ISkAnnot.anchor || ISkAnnot.start".
	 *
	 * Note: Correspond donc à XA.from(this.anchorNode).
	 * Note : ces annots ont aussi ISkAnnot.len === 0
	 */
	anchor?: IXAddr | null

	/**
	 * Offset d'insertion de l'annot lorsqu'elle est dans un interstice entre deux noeuds de la source.
	 * Utilisé uniquement pour les ISkAnnot
	 * - de type "MissingNode"
	 * - pour insérer des noeuds étrangers à l'item courant (diff...)
	 *
	 * Note 1: on a donc : this.start == XA.append(this.anchor, this.offset)
	 * Note 2: Pas utilisé pour les annots ancrés sur un fragment de string (characterData ou attr value), cf ISkTextAnnot.
	 */
	offset?: number

	level: EAnnotLevel

	/** */
	getLabel(): string

	/**
	 * Si cette propriété est présente cette annotation est en attente de résolution.
	 * Il faut écouter la promesse pour obtenir l'annotation définitive ou null si l'évaluation
	 * asynchrone conclut à l'absence d'annotation.
	 * Une fois la promesse résolue, result est renseigné, et cette prop future est passée à null.
	 */
	future?: Promise<ISkAnnot | null>

	/** Résultat de la promesse future lorsqu'il est connu, null si pas d'annot nécessaire. */
	result?: ISkAnnot | null

	/**
	 * Ajuste cette annot par le msg.
	 * Utilisé pour ajuster les foreignAnnots (diff).
	 */
	adjustBy?(msg: MsgOt): void

	equals(other: ISkAnnot): boolean

	toJSON(): any
}

/** Annots sur un noeud texte ou un attr. */
export interface ISkTextAnnot extends ISkAnnot {
	/**
	 * Offset dans le text/attr.
	 *
	 * Doit donc correspondre au dernier composant de ISkAnnot.start
	 * (pour cohérence IXAddrSeq.start et IXAddrSeq.length)
	 * NOTE : règle non respectée par DiffAnnotStrMark où ISkAnnot.start pointe le noeud text.
	 */
	offsetStart: number;
}

export function isSkTextAnnot(sk: ISkAnnot): sk is ISkTextAnnot {return sk && (typeof (sk as ISkTextAnnot).offsetStart === 'number')}


/**
 * Annot constituée de plusieurs séquences.
 */
export interface ISkSeqsAnnot extends ISkAnnot {
	/**
	 * ISkAnnot.start est le noeud container de toutes les séquences.
	 * L'annot concerne donc toujours cet unique noeud, d'où len == 1.
	 */
	len: 1

	/**
	 * Liste de séquences relatives à la racine ISkFragAnnot.start
	 */
	seqs: IXAddrSeq[]
}

export function isSkSeqsAnnot(sk: ISkAnnot): sk is ISkSeqsAnnot {return sk && Array.isArray((sk as ISkSeqsAnnot).seqs)}


export class EAnnotLevel {
	static search = new EAnnotLevel('search', 7);
	static diffadd = new EAnnotLevel('diffadd', 6);
	static diffrem = new EAnnotLevel('diffrem', 5);
	static diff = new EAnnotLevel('diff', 4);
	static error = new EAnnotLevel('error', 3);
	static warning = new EAnnotLevel('warning', 2);
	static info = new EAnnotLevel('info', 1);
	static diffaddHidden = new EAnnotLevel('diffadd', -1);
	static diffremHidden = new EAnnotLevel('diffrem', -1);
	static diffHidden = new EAnnotLevel('diff', -1);

	constructor(public readonly name: string, public readonly weight: number) {}

	max(other: EAnnotLevel): EAnnotLevel {return other && other.weight > this.weight ? other : this}

	//min(other: EAnnotLevel): EAnnotLevel {return other && other.weight < this.weight ? other : this}

}

/** Annotation relative à un contenu manquant. */
export interface ISkAnnotMissing extends ISkAnnot {
	/** Structure manquante. */
	structDef: ISkStructDef
}

export function isSkAnnotMissing(a: ISkAnnot): a is ISkAnnotMissing {return a && ('structDef' in a)}

/** Implémenté par les elts graphiques */
export interface ISkAnnotDrawer {

	/** Retourne true si l'affichage de l'annot a été pris en charge par ce composant graphique. */
	drawAnnot(annot: ISkAnnot): boolean;

	/** Retourne true si ce composant graphique gérait l'affichage de cette annotation. */
	eraseAnnot(annot: ISkAnnot): boolean;
}

export function isSkAnnotDrawer(sk: any): sk is ISkAnnotDrawer {return sk && (typeof sk.drawAnnot === 'function')}


/**
 * Objet graphique susceptible de mettre en évidence le détail d'une annotation.
 */
export interface ISkAnnotFocuser {

	/**
	 * @return true si le focus a été assumé par cette fonction.
	 */
	focusSkAnnot(annot: ISkAnnot): boolean
}

export function isSkAnnotFocuser(focuser: any): focuser is ISkAnnotFocuser {return focuser && ("focusSkAnnot" in focuser)}

/**
 *
 */
export abstract class SkAnnotBase {

	anchorNode: Node;

	get start() {return XA.from(this.anchorNode)}

	get len() {return 1}

	get level(): EAnnotLevel {return EAnnotLevel.error}

	abstract getLabel(): string;

	abstract equals(other: any): boolean;
}

/**
 * Annotation relative à un élement.
 */
export abstract class SkAnnotElt extends SkAnnotBase implements ISkAnnot {

	abstract type: string;

	equals(other: any) {return this.anchorNode === other.anchorNode && this.type === other.type}

	toJSON() {return {type: this.type, anchor: this.start}}

}

export class SkAnnotEltUnknown extends SkAnnotElt {
	static readonly TYPE = "eltUnknown";

	get type() {return SkAnnotEltUnknown.TYPE};

	structDef: ISkStructDef;

	init(anchorNode: Element, def?: ISkStructDef) {
		this.anchorNode = anchorNode;
		this.structDef = def;
		return this;
	}

	getLabel(): string {
		const label = this.structDef ? this.structDef.structLabel || this.structDef.structName || this.anchorNode.nodeName : this.anchorNode.nodeName;
		return `Élément "${label}" inconnu`;
	}
}

export class SkAnnotEltFree extends SkAnnotElt {
	static readonly TYPE = "elt";

	protected _level: EAnnotLevel;

	get level(): EAnnotLevel {return this._level}

	get type() {return SkAnnotEltFree.TYPE};

	label: string;

	init(anchorNode: Element, label: string, level?: EAnnotLevel) {
		this.anchorNode = anchorNode;
		this.label = label;
		this._level = level || EAnnotLevel.error;
		return this;
	}

	getLabel(): string {return this.label}

	equals(other: any) {return this.anchorNode === other.anchorNode && this.type === other.type && this.label === other.label}

}


export abstract class SkAnnotStr extends SkAnnotBase implements ISkAnnot {

	abstract type: string;

	label: string;

	equals(other: any) {return this.anchorNode === other.anchorNode && this.type === other.type}

	toJSON() {return {type: this.type, anchor: this.start}}
}

/** Noeud texte interdit à cet emplacement. */
export class SkAnnotTextForbidden extends SkAnnotStr {
	static readonly TYPE = "textForbidden";
	type = SkAnnotTextForbidden.TYPE;

	init(textNode: CharacterData): this {
		this.anchorNode = textNode;
		return this;
	}

	getLabel(): string {return "Texte interdit"}
}

/**
 * Annot pour la validation des chaines de caractères.
 * Pour les noeuds texte, la validation est ancrée sur l'élément parent
 * car il est interdit d'avoir un texte typé combiné avec d'autres éléments
 * (http://relaxng.org/spec-20011203.html#string-sequences). Permet de traiter
 * l'absence de noeuds textes ou un noued texte "" de façon transparente.
 */
export class SkAnnotWrongValue extends SkAnnotStr {
	static readonly TYPE = "wrongVal";
	type = SkAnnotWrongValue.TYPE;

	label: string;

	protected _level: EAnnotLevel;

	get level(): EAnnotLevel {return this._level}

	init(container: Element | Attr, label: string, level?: EAnnotLevel): this {
		this.anchorNode = container;
		this.label = label;
		this._level = level || EAnnotLevel.error;
		return this;
	}

	getLabel(): string {return this.label}

	equals(other: any) {return this.anchorNode === other.anchorNode && this.type === other.type && this.label === other.label}

	toJSON() {return {type: this.type, anchor: this.start, label: this.label}}
}


/**
 * Annotation relative à un attribut existant.
 */
export abstract class SkAnnotAttr extends SkAnnotBase implements ISkAnnot {

	abstract type: string;

	equals(other: any) {return this.anchorNode === other.anchorNode && this.type === other.type}

	toJSON() {return {type: this.type, anchor: this.start}}
}

export class SkAnnotAttrUnknown extends SkAnnotAttr {
	static readonly TYPE = "attUnknown";

	get type() {return SkAnnotAttrUnknown.TYPE}

	structDef: ISkStructDef;

	/**
	 * @param Element Dom du XML logique
	 * @param def Définition de l'attribut inconnu (généralement null).
	 */
	init(anchorNode: Attr, def?: ISkStructDef): this {
		this.anchorNode = anchorNode;
		this.structDef = def;
		return this;
	}

	getLabel() {
		const label = this.structDef ? this.structDef.structLabel || this.structDef.structName : null;
		return `Attribut '${label || this.anchorNode.nodeName}' inconnu`;
	}
}

/**
 * Annotation relative à un attribut manquant.
 */
export class SkAnnotAttrMissing extends SkAnnotAttr {
	static readonly TYPE = "attrMissing";

	get type() {return SkAnnotAttrMissing.TYPE}

	/** Element de rattachement de l'attribut. */
	attName: string;

	structDef: ISkStructDef;

	/**
	 * @param anchorNode Element de l'attribut manquant.
	 * @param def Définition de l'attribut.
	 */
	init(anchorNode: Element, def: ISkStructDef): this {
		this.anchorNode = anchorNode;
		this.structDef = def;
		this.attName = this.structDef.structName; //quid si structDef fuzzy ?
		return this;
	}

	get start() {return XA.append(XA.fromNode(this.anchorNode), this.attName)}

	get anchor() {return XA.from(this.anchorNode)}

	get len() {return 0};

	getLabel() {
		const label = this.structDef.structLabel || this.structDef.structName;
		return `Attribut manquant : ${label}`
	}

	equals(other: any) {return this.anchorNode === other.anchorNode && this.type === other.type && this.attName === other.attName}

	toJSON() {return {type: this.type, anchor: this.anchor, name: this.attName}}
}

/**
 * Annotation relative à un noeud (élément ou texte) manquant ou une structure (group, choice, unorderedGroup) manquante.
 *
 * @see SkAnnotAttrMissing
 */
export class SkAnnotMissing extends SkAnnotBase implements ISkAnnotMissing {
	static readonly TYPE_eltMissing = "eltMissing";
	static readonly TYPE_choiceMissing = "choiceMissing";
	static readonly TYPE_groupMissing = "groupMissing";
	static readonly TYPE_ugroupMissing = "ugroupMissing";

	type: string;
	offset: number;
	structDef: ISkStructDef;
	customLabel: string;

	/**
	 * @param type
	 * @param anchorNode Parent de l'objet manquant.
	 * @param offset Offset dans les fils de la structure manquante.
	 * @param def
	 */
	init(type: string, anchorNode: Node, offset: number, def: ISkStructDef, customLabel?: string): this {
		this.type = type;
		this.anchorNode = anchorNode;
		this.offset = offset;
		this.structDef = def;
		this.customLabel = customLabel;
		return this;
	}

	get anchor() {return XA.from(this.anchorNode)}

	get start() {return XA.append(XA.from(this.anchorNode), this.offset)}

	get len() {return 0};

	getLabel() {return this.customLabel || this.structDef.structLabel ? `Contenu manquant: ${this.structDef.structLabel}` : `Contenu manquant`}

	equals(other: any) {
		return this.anchorNode === other.anchorNode && this.type === other.type && this.offset === other.offset && this.structDef.structName === other.structDef.structName;
	}

	toJSON() {return {type: this.type, anchor: this.anchor, offset: this.offset, name: this.structDef.structName}}
}
